import React, { useState, useCallback, useEffect } from 'react';
import { useAgentStream, AgentUsage } from '../../hooks/useAgentStream';
import { UsageDashboard } from './UsageDashboard';
import { ChatMessages, Message } from './ChatMessages';
import { ChatInput } from './ChatInput';
import { DocumentSidebar, Session } from './DocumentSidebar';
import { LlmModal } from './LlmModal';
import { RefineModal, RefineData, RefineChoice, RefineResult } from './RefineModal';
import { MobileSheet } from './MobileSheet';
import { agentFetch } from '../../utils/agentFetch';

interface UserInfo {
  role: string;
  org_id: string;
  display_name: string;
  email: string;
}

interface OrgSpend {
  org_name: string;
  total_spend_gbp: number;
  spend_cap_gbp: number;
}

interface Props {
  userInfo: UserInfo | null;
}

export const ChatInterface: React.FC<Props> = ({ userInfo }) => {
  const { streamText, isStreaming, send } = useAgentStream();

  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showLlmModal, setShowLlmModal] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  // Refine my question — optional step before the LLM picker
  const [refineEnabled, setRefineEnabled] = useState<boolean>(() => {
    try { return localStorage.getItem('rdf_refine_question') === '1'; } catch { return false; }
  });
  const [isRefining, setIsRefining] = useState(false);
  const [refineSlow, setRefineSlow] = useState(false);
  const [refineNotice, setRefineNotice] = useState<string | null>(null);
  const [refineData, setRefineData] = useState<RefineData | null>(null);
  const [pendingOriginal, setPendingOriginal] = useState<string | null>(null);
  // Chat folder selection when the refine pop-up opened (baseline for "Also searched")
  const [refineBaseIds, setRefineBaseIds] = useState<Set<string>>(new Set());
  const [pendingAlsoSearched, setPendingAlsoSearched] = useState<string[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sessionId, setSessionId] = useState<string>(() => crypto.randomUUID());
  const [sessionTitle, setSessionTitle] = useState<string | null>(null);
  const [showMobileSheet, setShowMobileSheet] = useState(false);
  const [capExceeded, setCapExceeded] = useState(false);
  const [capMessage, setCapMessage] = useState('');
  const [orgSpend, setOrgSpend] = useState<OrgSpend | null>(null);
  const [latestUsage, setLatestUsage] = useState<AgentUsage | null>(null);
  const [sessionTotals, setSessionTotals] = useState({
    claude: { tokens: 0, cost: 0 },
    gemini: { tokens: 0, cost: 0 },
  });
  const isSuperAdmin = userInfo?.role === 'super_admin';

  // Load sessions on mount
  const loadSessions = useCallback(async () => {
    try {
      const r = await agentFetch('/api/sessions');
      if (r.ok) setSessions(await r.json());
    } catch (_) {}
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  // Check spend cap on mount
  useEffect(() => {
    agentFetch('/admin/api/my-org-spend')
      .then(r => r.ok ? r.json() : null)
      .then((data: OrgSpend | null) => {
        if (data) setOrgSpend(data);
        if (data && data.total_spend_gbp >= data.spend_cap_gbp) {
          setCapExceeded(true);
          const cap = data.spend_cap_gbp != null ? `£${data.spend_cap_gbp.toFixed(2)}` : 'its limit';
          setCapMessage(`Your organisation has reached its ${cap} spend cap. Please contact rav@therdf.co.uk to increase it.`);
        }
      })
      .catch(() => {});
  }, []);

  const autoSave = useCallback(async (history: Message[], title: string) => {
    if (!history.length) return;
    try {
      await agentFetch('/api/sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          title,
          messages: history.map(m => ({ role: m.role, content: m.content })),
        }),
      });
    } catch (_) {}
  }, [sessionId]);

  // Failure notice clears itself after a few seconds
  useEffect(() => {
    if (!refineNotice) return;
    const t = setTimeout(() => setRefineNotice(null), 6000);
    return () => clearTimeout(t);
  }, [refineNotice]);

  // Re-read org spend after anything billable (chat answers and refines)
  const refreshOrgSpend = useCallback(() => {
    agentFetch('/admin/api/my-org-spend')
      .then(r => r.ok ? r.json() : null)
      .then((data: OrgSpend | null) => {
        if (data) setOrgSpend(data);
        if (data && data.total_spend_gbp >= data.spend_cap_gbp) {
          setCapExceeded(true);
          const cap = `£${data.spend_cap_gbp.toFixed(2)}`;
          setCapMessage(`Your organisation has reached its ${cap} spend cap. Please contact rav@therdf.co.uk to increase it.`);
        }
      })
      .catch(() => {});
  }, []);

  // One refine round-trip. Books the (billed) Gemini usage into the session
  // totals, and handles the spend cap. Never throws.
  const runRefine = useCallback(async (text: string, ids: Set<string>): Promise<RefineResult> => {
    const controller = new AbortController();
    // Generous: a cold start (first request after a quiet spell) can take 30s+
    const timer = setTimeout(() => controller.abort(), 40000);
    try {
      const r = await agentFetch('/api/chat/refine', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          filters: Array.from(ids),
          history: messages.map(m => ({ role: m.role, content: m.content })),
        }),
        signal: controller.signal,
      });
      if (r.status === 402) {
        setCapExceeded(true);
        setMessages(prev => [...prev, {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: 'Your organisation has reached its spend cap. Queries are temporarily blocked. Please contact rav@therdf.co.uk to increase the cap.',
        }]);
        return { data: null, status: 402 };
      }
      if (!r.ok) return { data: null, status: r.status };
      const data: RefineData = await r.json();
      const usage = data.usage;
      if (usage && (usage.total_tokens || usage.cost_gbp)) {
        setSessionTotals(prev => ({
          ...prev,
          gemini: {
            tokens: prev.gemini.tokens + (usage.total_tokens || 0),
            cost: prev.gemini.cost + (usage.cost_gbp || 0),
          },
        }));
        if ((usage.cost_gbp || 0) > 0) refreshOrgSpend();
      }
      return { data };
    } catch (_) {
      return { data: null, status: 0 };
    } finally {
      clearTimeout(timer);
    }
  }, [messages, refreshOrgSpend]);

  const handleRefineToggle = useCallback((enabled: boolean) => {
    setRefineEnabled(enabled);
    try { localStorage.setItem('rdf_refine_question', enabled ? '1' : '0'); } catch { /* ignore */ }
  }, []);

  const handleSend = useCallback(async () => {
    if (isStreaming || isRefining || capExceeded || !inputValue.trim()) return;
    if (selectedIds.size === 0) {
      alert('Please select at least one document folder from the library to proceed.');
      return;
    }
    const text = inputValue.trim();
    setInputValue('');
    setRefineNotice(null);

    // Optional refine step — never blocks the query: on any error, timeout
    // or empty result we carry on with the original wording.
    if (refineEnabled) {
      setIsRefining(true);
      setRefineSlow(false);
      const slowTimer = setTimeout(() => setRefineSlow(true), 8000);
      const { data, status } = await runRefine(text, selectedIds);
      clearTimeout(slowTimer);
      setIsRefining(false);
      setRefineSlow(false);
      if (status === 402) return;
      if (data?.suggestions?.length) {
        setPendingOriginal(text);
        setRefineBaseIds(new Set(selectedIds));
        setRefineData(data);
        return;
      }
      if (!data || data.refine_failed) {
        setRefineNotice("Couldn't suggest refinements — using your original question.");
      }
    }

    setPendingOriginal(null);
    setPendingMessage(text);
    setShowLlmModal(true);
  }, [isStreaming, isRefining, capExceeded, inputValue, selectedIds, refineEnabled, runRefine]);

  const handleRefineSelect = useCallback((choice: RefineChoice | null) => {
    setRefineData(null);
    if (!choice) { setPendingOriginal(null); return; }
    // Apply the pop-up's folder selection (incl. any suggested folders on the
    // picked card) — the sidebar ticks update from this state, and the stream
    // call reads it after the LLM pick.
    setSelectedIds(new Set(choice.folderIds));
    setPendingAlsoSearched(choice.addedLabels);
    setPendingMessage(choice.question);
    setShowLlmModal(true);
  }, []);

  const handleModelSelect = useCallback(async (model: string | null) => {
    setShowLlmModal(false);
    if (!model || !pendingMessage) {
      setPendingMessage(null); setPendingOriginal(null); setPendingAlsoSearched([]);
      return;
    }

    const userMsg: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: pendingMessage,
      ...(pendingOriginal && pendingOriginal !== pendingMessage ? { originalQuestion: pendingOriginal } : {}),
      ...(pendingAlsoSearched.length ? { alsoSearched: pendingAlsoSearched } : {}),
    };
    const title = sessionTitle || pendingMessage.slice(0, 70);
    if (!sessionTitle) setSessionTitle(title);

    setMessages(prev => [...prev, userMsg]);

    const historyForApi = messages.map(m => ({ role: m.role, content: m.content }));
    historyForApi.push({ role: 'user', content: pendingMessage });

    await send(
      {
        message: pendingMessage,
        filters: Array.from(selectedIds),
        llm: model,
        history: messages.map(m => ({ role: m.role, content: m.content })),
      },
      {
        onDone: (rawText, usageData, spendWarning) => {
          const aiMsg: Message = {
            id: crypto.randomUUID(),
            role: 'assistant',
            content: rawText,
            model,
            spendWarning: spendWarning || undefined,
          };
          setMessages(prev => {
            const updated = [...prev, aiMsg];
            autoSave(updated, title);
            loadSessions();
            return updated;
          });
          if (usageData) {
            setLatestUsage(usageData);
            setSessionTotals(prev => {
              const isGemini = model.includes('gemini');
              const bucket = isGemini ? 'gemini' : 'claude';
              return {
                ...prev,
                [bucket]: {
                  tokens: prev[bucket].tokens + (usageData.total_tokens || 0),
                  cost: prev[bucket].cost + (usageData.cost_gbp || 0),
                },
              };
            });
          }
          if (usageData && usageData.cost_gbp > 0) {
            // Re-check cap after query
            refreshOrgSpend();
          }
        },
        onError: (code, msg) => {
          let displayMsg = msg;
          if (code === 'SPEND_CAP_EXCEEDED' || code === '402') {
            displayMsg = 'Your organisation has reached its spend cap. Queries are temporarily blocked. Please contact rav@therdf.co.uk to increase the cap.';
            setCapExceeded(true);
          } else if (code === '429') {
            displayMsg = 'Please wait a moment before sending another query.';
          } else if (code === '413') {
            displayMsg = 'Your query selected too many documents and exceeded the 50,000 token limit. Please deselect some folders and try again.';
          } else if (code === 'overloaded_error' || msg.toLowerCase().includes('overload')) {
            displayMsg = 'The AI service is currently overloaded. Please wait a moment and try again.';
          }
          const errMsg: Message = {
            id: crypto.randomUUID(),
            role: 'assistant',
            content: displayMsg,
          };
          setMessages(prev => [...prev, errMsg]);
        },
      },
    );

    setPendingMessage(null);
    setPendingOriginal(null);
    setPendingAlsoSearched([]);
  }, [pendingMessage, pendingOriginal, pendingAlsoSearched, messages, selectedIds, send, autoSave, loadSessions, sessionTitle, refreshOrgSpend]);

  const handleNewConversation = useCallback(() => {
    setMessages([]);
    setSessionId(crypto.randomUUID());
    setSessionTitle(null);
    loadSessions();
  }, [loadSessions]);

  const handleSessionRestore = useCallback(async (id: string) => {
    try {
      const r = await agentFetch(`/api/sessions/${id}`);
      if (!r.ok) return;
      const session = await r.json();
      setSessionId(id);
      setSessionTitle(session.title);
      const restored: Message[] = (session.messages || []).map((m: any) => ({
        id: crypto.randomUUID(),
        role: m.role,
        content: m.content,
      }));
      setMessages(restored);
    } catch (_) {}
  }, []);

  return (
    <div className="ai-chat-wrapper">
      <div className="ai-chat-layout">
        {/* Sidebar — hidden on mobile */}
        <DocumentSidebar
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          onSessionRestore={handleSessionRestore}
          sessions={sessions}
          onSessionsReload={loadSessions}
        />

        {/* Main chat area */}
        <div className="ai-chat-main">
          <ChatMessages
            messages={messages}
            streamText={streamText}
            isStreaming={isStreaming}
            onSuggestionClick={text => { setInputValue(text); }}
            onNewConversation={handleNewConversation}
          />
          <ChatInput
            value={inputValue}
            onChange={setInputValue}
            onSend={handleSend}
            onOpenMobileFilter={() => setShowMobileSheet(true)}
            isStreaming={isStreaming}
            capExceeded={capExceeded}
            capMessage={capMessage}
            refineEnabled={refineEnabled}
            onRefineToggle={handleRefineToggle}
            isRefining={isRefining}
            refineSlow={refineSlow}
            refineNotice={refineNotice}
          />
        </div>

        {/* Refine my question modal — shown before the LLM picker */}
        {refineData && (
          <RefineModal
            data={refineData}
            initialSelectedIds={refineBaseIds}
            onRecheck={runRefine}
            onSelect={handleRefineSelect}
          />
        )}

        {/* LLM selection modal */}
        {showLlmModal && (
          <LlmModal
            isSuperAdmin={isSuperAdmin}
            onSelect={handleModelSelect}
            orgSpend={orgSpend}
          />
        )}

        {/* Mobile document filter sheet */}
        <MobileSheet
          open={showMobileSheet}
          onClose={() => setShowMobileSheet(false)}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          onSessionRestore={handleSessionRestore}
          sessions={sessions}
          onSessionsReload={loadSessions}
        />
      </div>

      {/* Usage bar — spans full width below both columns */}
      <UsageDashboard
        latestUsage={latestUsage}
        sessionTotals={sessionTotals}
        isSuperAdmin={isSuperAdmin}
        orgSpend={orgSpend}
      />
    </div>
  );
};
